import { ImageLoaderProps } from 'next/legacy/image';
import { MELEVEN_BASE_URL } from '@frontastic-engbers/helpers/constants/meleven';
import { convertImageToWebp, isCloudinaryUrl, isMelevenUrl } from '@frontastic-engbers/helpers/utils/meleven';

function normalizeSrc(src: string): string {
  return src[0] === '/' ? src.slice(1) : src;
}

export function frontasticCloudinaryLoader({ src, width }: ImageLoaderProps): string {
  const cloudName = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME; //cloudinary cloud name
  const root = `https://res.cloudinary.com/${cloudName}/image/upload`; //base path

  const query = (src as string).split('?')[1]; //contains the query parameters
  const queries = new URLSearchParams(query); //injected query params

  const ratio = queries.get('ratio'); //aspect asset ratio
  const gravity = queries.get('gravity'); //required gravity: faces, body, etc..
  const x = queries.get('x__coord'); //x coordinate for custom gravity
  const y = queries.get('y__coord'); //y coordinate for custom gravity

  //cloudinary transformations
  const params = ratio
    ? [
      ...['f_auto', 'c_limit', 'g_' + (gravity ?? 'auto')],
      ...(width ? ['w_' + width, 'q_' + 'auto', 'c_crop'] : []),
      ...(gravity === 'custom' ? ['x_' + x, 'y_' + y] : []),
      ...(ratio ? ['ar_' + ratio] : []),
    ]
    : [...(width ? ['w_' + width, 'q_' + 'auto', 'c_scale'] : [])];

  const transformations = params.join(',');

  //image source with the query paremeters removed
  const source = (src as string).split('?')[0];

  return `${root}/${transformations}/${normalizeSrc(source)}${isCloudinaryUrl(root) ? '.webp' : ''}`;
}

export const melevenLoader = ({ src, width, quality }: ImageLoaderProps): string => {
  const baseUrl = MELEVEN_BASE_URL;
  const convertedSrc = convertImageToWebp(src);

  const parts = convertedSrc.replace(baseUrl, '').replace(/^\/+/, '').split('/');
  const [transformationsPart, filename] = parts.slice(-2);
  let transformations = transformationsPart;

  const wMatch = transformations.match(/w_(\d+)/);
  const hMatch = transformations.match(/h_(\d+)/);

  if (wMatch && hMatch) {
    const originalWidth = +wMatch[1];
    const originalHeight = +hMatch[1];
    const newHeight = Math.round(originalHeight * (width / originalWidth));
    transformations = transformations.replace(/w_\d+/, `w_${width}`).replace(/h_\d+/, `h_${newHeight}`);
  }

  if (quality) {
    transformations = transformations.includes('q_')
      ? transformations.replace(/q_\d+/, `q_${quality}`)
      : `${transformations},q_${quality}`;
  }

  return `${baseUrl}${transformations}/${filename}`;
};

const DEFAULT_WIDTHS = [320, 640, 750, 1080, 1200];

export const melevenLoaderSrcSet = (src: string, widths: number[] = DEFAULT_WIDTHS, quality?: number) => {
  if (!isMelevenUrl(src)) {
    return src;
  }

  return widths.map((w) => {
    const url = melevenLoader({
      src,
      width: w,
      quality
    });

    return `${url} ${w}w`;
  }).join(', ');
};
